import type { InboxEntry, WorkNoticeDelivery } from "@beacon/core/beacon-inbox"
import { inboxWords } from "@beacon/core/beacon-inbox/words"
import { describeRelativeTime } from "@/time/describe.ts"

/*
 * The Beacon inbox, as the web app says it: each entry's line, in the words a push or a Slack DM
 * says it in (`inboxWords`), and where the Member's notices alert them. The web API decides what an
 * entry shows, rechecked through the Member's access as they read it: what they can no longer open
 * is said to be a ticket or a change request, and nothing more.
 */

/**
 * What the entry says: "Ada Lovelace assigned you to OPS-12", "Your change request OPS change
 * request 3 has new Questions for you"; for what the Member can no longer open, only what kind of
 * thing it was about.
 */
export const entryLineOf = (entry: InboxEntry) => inboxWords(entry.notice).line

/** What an entry's row shows as its glyph: what it is about, or the reason it most says. */
export type EntryMark =
  "assigned" | "mentioned" | "commented" | "change-request" | "account" | "unavailable"

export function entryMarkOf({ notice }: InboxEntry): EntryMark {
  if (notice.kind !== "ticket") return notice.kind
  if (notice.reasons.includes("assigned")) return "assigned"
  if (notice.reasons.includes("mentioned")) return "mentioned"
  return "commented"
}

/** Which entries opening `entry` reads: itself while unread, none once read. */
export const readAsOpened = (entry: InboxEntry): string[] => (entry.read ? [] : [entry.id])

/**
 * What the entry is about, and when: "Rotate the keys · Operations · 5m ago"; what a Member merge
 * did; only when, for what the Member can no longer open.
 */
export function describeEntry(entry: InboxEntry, now: Date) {
  const when = describeRelativeTime(entry.at, now)
  const { notice } = entry
  if (notice.kind === "unavailable") return when
  if (notice.kind === "account") return [inboxWords(notice).about, when].filter(Boolean).join(" · ")
  const { title, project } = notice.kind === "ticket" ? notice.ticket : notice.changeRequest
  return [title, project.archived ? `${project.name} (archived)` : project.name, when].join(" · ")
}

/** How many unread notices, for a screen reader after a count: "3 unread notices". */
export const describeInboxUnread = (count: number) =>
  count <= 0 ? "" : count === 1 ? "1 unread notice" : `${count} unread notices`

/** Where the Member's notices may alert them, as the settings offer it: Beacon first, the default. */
export const workNoticeDeliveryChoices: readonly {
  value: WorkNoticeDelivery
  label: string
  detail: string
}[] = [
  {
    value: "beacon",
    label: "Beacon",
    detail:
      "Notifications on the devices you turned them on for, following your choices above. Nothing in Slack.",
  },
  {
    value: "slack",
    label: "Slack",
    detail:
      "A direct message from Beacon in Slack, and nothing from Beacon itself. Your inbox lists them either way.",
  },
]
